/************************************************************************************

* [MODULE]: PRODUCT INFORMATION
  -- Main features:
  + l-product-form
  + l-variant-selects
  + l-product-controls
  + l-product-quantity-input

*************************************************************************************/
if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			quantityInput = undefined
			quantityForm = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Initialize the buttons state
				this.initializeProductSwapUtility()

				// Data
				this.selectedSellingPlanId = this.dataset.sellingPlanId

				// Dispatch events
				this.dispatchEvent(new CustomEvent('l:product-info:loaded', { bubbles: true }))

				// Catch variant picker options changed events
				document.addEventListener('l:variant:option-changed', (event) => {
					const { target, selectedOptionValues } = event.detail
					if (!this.contains(target)) return

					this.handleOptionValueChange(target, selectedOptionValues, null)
				})

				// Catch selling plan changed events
				document.addEventListener('l:sellingPlan:changed', (event) => {
					const { sellingPlanId } = event.detail
					const variant = this.getSelectedVariant(this)
					this.selectedSellingPlanId = sellingPlanId

					this.handleOptionValueChange(event.target, null, variant)
				})
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			handleOptionValueChange(target, selectedOptionValues, variant) {
				// Reset form data
				this.resetProductFormState()

				// Check new selected status / URLs
				const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url

				this.pendingRequestUrl = productUrl
				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

				// Render product information based on new selected options
				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variant, shouldFetchFullPage),
					targetId: target.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			resetProductFormState() {
				if (this.productForm) {
					this.productForm.global_toggleSubmitButton(true, null, false, true)
					this.productForm.handleErrorMessage()
				}
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				this.abortController?.abort()
				this.abortController = new AbortController()

				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')

						callback(html)
					})
					.then(() => {
						// set focus to last clicked option value
						document.querySelector(`#${targetId}`)?.focus()
					})
					.catch((error) => {
						console.error(error)
					})
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))
					this.updateURL(productUrl, variant?.id)

					if (updateFullPage) {
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML

						HTMLUpdateUtility.viewTransition(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						HTMLUpdateUtility.viewTransition(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)

					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)

					if (!variant) {
						this.setUnavailable()

						return
					}

					const updateSourceFromDestination = (id, htmlMethod = 'innerHTML', shouldHide = (source) => false) => {
						const source = html.getElementById(`${id}-${this.sectionId}`)
						const destination = document.getElementById(`${id}-${this.dataset.section}`)

						if (source && destination) {
							destination[htmlMethod] = source[htmlMethod]
							destination.classList.toggle('l-hidden', shouldHide(source))
						}
					}

					updateSourceFromDestination('price', 'outerHTML')
					updateSourceFromDestination('stockInfo', 'outerHTML')
					updateSourceFromDestination('Subscriptions', 'outerHTML')
					updateSourceFromDestination('stickyATC', 'outerHTML')
					updateSourceFromDestination('ProductSubmitButton')

					this.productForm?.global_toggleSubmitButton(
						html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
						'soldOut'
					)

					this.dispatchEvent(
						new CustomEvent('l:variant:changed', {
							detail: { sectionId: this.sectionId, html, variant },
							bubbles: true,
						})
					)
				}
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')

				if (variantSelects) {
					HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				const params = new URLSearchParams()
				if (variantId) params.append('variant', variantId)
				if (this.selectedSellingPlanId) params.append('selling_plan', this.selectedSellingPlanId)
				const newUrl = `${url}?${params.toString()}`

				window.history.replaceState({}, '', newUrl)
			}

			updateVariantInputs(variantId) {
				this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			setUnavailable() {
				this.productForm?.global_toggleSubmitButton(true, 'unavailable')

				const selectors = ['price', 'stockInfo', 'Subscriptions', 'stickyATC']
					.map((id) => `#${id}-${this.dataset.section}`)
					.join(', ')

				document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('l-hidden'))
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('l-variant-selects [data-selected-variant]')?.innerHTML
				return !!selectedVariant ? JSON.parse(selectedVariant) : null
			}

			buildRequestUrlWithParams(url, optionValues, variant, shouldFetchFullPage = false) {
				const params = []

				!shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

				if (this.selectedSellingPlanId) params.push(`selling_plan=${this.selectedSellingPlanId}`)

				if (optionValues?.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				} else if (variant) {
					params.push(`variant=${variant.id}`)
				}

				return `${url}?${params.join('&')}`
			}

			get productForm() {
				return this.querySelector('l-product-form')
			}

			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LTAProductForm extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.form = this.querySelector('form') || document.getElementById(this.dataset.formId)
				this.cart = document.getElementById('CartDrawer')
				this.submitButton = this.querySelector('[type="submit"]')
				this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-label]')

				// Set data / attribute
				this.sectionId = this.dataset.sectionId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Setup event
				this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
			}

			onSubmitHandler(evt) {
				evt.preventDefault()

				if (this.submitButton.getAttribute('aria-disabled') === 'true') return

				// Setup button loading state
				this.submitButton.setAttribute('aria-disabled', true)
				this.submitButton.classList.add('is-loading')

				// Setup adding product to cart
				const config = L_ATC.handleConfigWithFormEl(this.form)

				L_ATC.addToCart(config, 'l-product-form')
					.catch((e) => {
						console.error(e)
					})
					.finally(() => {
						// Remove button loading state
						this.submitButton.classList.remove('is-loading')
						if (!this.error) this.submitButton.removeAttribute('aria-disabled')
					})
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage
			}

			global_toggleSubmitButton(disable = true, textType, needToUpdateText = true, temporaryDisabled = false) {
				console.log({ disable, textType, needToUpdateText, temporaryDisabled })
				this.submitButton.classList.toggle('is-temporary-disabled', temporaryDisabled)

				if (disable) {
					this.submitButton.setAttribute('disabled', 'disabled')
					if (textType) this.submitButtonText.textContent = this.getButtonLabel(textType)

					return
				}

				this.submitButton.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonText.textContent = this.getButtonLabel('addToCart')
				}
			}

			getButtonLabel(textType = 'addToCart') {
				switch (textType) {
					case 'addToCart':
						return this.dataset.atcLabel || window.theme.variantStrings.addToCart
					case 'soldOut':
						return this.dataset.soldOutLabel || window.theme.variantStrings.soldOut
					case 'preOrder':
						return this.dataset.preOrderLabel || window.theme.variantStrings.preOrder
					case 'unavailable':
						return this.dataset.unavailableLabel || window.theme.variantStrings.unavailable
					default:
						return window.theme.variantStrings.addToCart
				}
			}

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	customElements.define(
		'l-variant-selects',
		class LTAVariantSelect extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addEventListener('change', (event) => {
					const target = this.getInputForEventTarget(event.target)

					this.updateSelectionMetadata(event)

					document.dispatchEvent(
						new CustomEvent('l:variant:option-changed', {
							detail: {
								event,
								target,
								selectedOptionValues: this.selectedOptionValues,
							},
						})
					)
				})
			}

			updateSelectionMetadata({ target }) {
				const { value, tagName } = target

				if (tagName === 'SELECT' && target.selectedOptions.length) {
					return
				}

				// Update selected option value
				if (target.hasAttribute('data-product-variant-input')) {
					const selectedSwatchValue = target.closest('[data-variant-options]').querySelector('[data-selected-value]')
					if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
				}
			}

			getInputForEventTarget(target) {
				return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
			}

			get selectedOptionValues() {
				return Array.from(
					this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
				).map(({ dataset }) => dataset.optionValueId)
			}
		}
	)
}

if (!customElements.get('l-product-quantity-input')) {
	customElements.define(
		'l-product-quantity-input',
		class LTAProductQuantityInput extends HTMLElement {
			constructor() {
				super()
			}

			quantityUpdateUnsubscriber = undefined

			connectedCallback() {
				// Elements
				this.input = this.querySelector('input')

				// Setup events
				this.changeEvent = new Event('change', { bubbles: true })

				this.input.addEventListener('change', () => {
					this.validateQtyRules()
				})

				this.querySelectorAll('button').forEach((button) =>
					button.addEventListener('click', this.onButtonClick.bind(this))
				)

				// Subscribe global event
				document.addEventListener('l:quantity:update', this.validateQtyRules.bind(this))

				// Validate Quantity Rules
				this.validateQtyRules()
			}

			onButtonClick(event) {
				event.preventDefault()

				const previousValue = this.input.value

				if (event.target.name === 'plus') {
					// Reset value to minimum value || increase value based on step
					const needToResetMinimumValue =
						parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0

					needToResetMinimumValue ? (this.input.value = this.input.dataset.min) : this.input.stepUp()
				} else {
					this.input.stepDown()
				}

				// Dispatch event
				if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

				// Reset to minimum value
				if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
					this.input.value = parseInt(this.input.min)
				}
			}

			validateQtyRules() {
				const value = parseInt(this.input.value)

				if (this.input.min) {
					const buttonMinus = this.querySelector("button[name='minus']")
					buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
				}

				if (this.input.max) {
					const max = parseInt(this.input.max)
					const buttonPlus = this.querySelector("button[name='plus']")
					buttonPlus.classList.toggle('disabled', value >= max)
				}
			}
		}
	)
}

if (!customElements.get('l-product-controls')) {
	customElements.define(
		'l-product-controls',
		class LTAProductInfo extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.quantityFormEl = this.querySelector('l-product-quantity-input')
				this.quantityInputEl = this.quantityFormEl?.querySelector('input')
				this.productForm = this.querySelector('l-product-form')

				if (!this.quantityInputEl || !this.quantityFormEl) return

				// Subscribe global event
				document.addEventListener('l:variant:changed', this.updateQuantityRules.bind(this))

				// Setup validate quantity controls
				this.setQuantityBoundries()
			}

			setQuantityBoundries() {
				// Get data from l-product-quantity-input element
				const quantityDataset = this.quantityInputEl.dataset
				const data = {
					cartQuantity: quantityDataset.cartQuantity ? parseInt(quantityDataset.cartQuantity) : 0,
					min: quantityDataset.min ? parseInt(quantityDataset.min) : 1,
					max: quantityDataset.max ? parseInt(quantityDataset.max) : null,
					step: this.quantityInputEl.step ? parseInt(this.quantityInputEl.step) : 1,
				}

				// Get min-max quantity of selected variant based on variant in current cart
				let min = data.min
				const max = data.max === null ? data.max : data.max - data.cartQuantity
				if (max !== null) min = Math.min(min, max)
				if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

				// Set values for quantity input element (min, max, value)
				this.quantityInputEl.min = min
				this.quantityInputEl.value = min
				max ? (this.quantityInputEl.max = max) : this.quantityInputEl.removeAttribute('max')

				// Publish quantity update
				document.dispatchEvent(new CustomEvent('l:quantity:update', { detail: { id: this.dataset.section } }))
			}

			updateQuantityRules(event) {
				const { html, sectionId } = event.detail

				const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
				const selectors = ['[data-product-quantity-input]', '[data-quantity-label]']

				for (let selector of selectors) {
					const current = this.querySelector(selector)
					const updated = quantityFormUpdated.querySelector(selector)
					if (!current || !updated) continue

					// Update product quantity input || other elements
					if (selector === '[data-product-quantity-input]') {
						const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
						for (let attribute of attributes) {
							const valueUpdated = updated.getAttribute(attribute)
							valueUpdated !== null ? current.setAttribute(attribute, valueUpdated) : current.removeAttribute(attribute)
						}
					} else {
						current.innerHTML = updated.innerHTML
					}
				}

				// Update and dispatch quantity changed event
				this.setQuantityBoundries()
			}

			// b2b__fetchQuantityRules() {
			// 	// Get current selected variant ID
			// 	const currentVariantId = this.productForm?.variantIdInput?.value
			// 	if (!currentVariantId) return

			// 	// Call API to update rules cart component
			// 	this.querySelector('[data-rules-cart]').classList.add('is-loading')

			// 	fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
			// 		.then((response) => response.text())
			// 		.then((responseText) => {
			// 			const html = new DOMParser().parseFromString(responseText, 'text/html')
			// 			this.updateQuantityRules(this.dataset.section, html)
			// 		})
			// 		.catch((e) => console.error(e))
			// 		.finally(() => this.querySelector('[data-rules-cart]').classList.remove('is-loading'))
			// }
		}
	)
}

if (!customElements.get('l-product-installments')) {
	customElements.define(
		'l-product-installments',
		class LTAProductsInstallments extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Subscribe global event
				document.addEventListener('l:variant:changed', (event) => {
					const variantId = event.detail.variant.id

					const input = this.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}
		}
	)
}

if (!customElements.get('l-product-back-in-stock-form')) {
	class LTAProductBackInStockForm extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Elements
			this.formEl = this.querySelector('form')
			this.successTextEl = this.querySelector('[data-success-text]')
			this.errorTextEl = this.querySelector('[data-error-text]')

			// Data
			this.selectedVariantId = this.dataset.selectedVariantId
			this.publicKlavyioId = this.dataset.publicKlavyioId

			// Events
			this.formEl.addEventListener('submit', this.handleSubmitEvent.bind(this))
			document.addEventListener('l:variant:changed', (event) => {
				this.selectedVariantId = event.detail.variant.id
			})
		}

		handleSubmitEvent(event) {
			event.preventDefault()

			const formData = new FormData(this.formEl)
			const email = formData.get('email')
			const phone = formData.get('phone')

			const payload = {
				data: {
					type: 'back-in-stock-subscription',
					attributes: {
						profile: {
							data: {
								type: 'profile',
								attributes: {
									email,
									phone_number: phone,

									// subscriptions: {
									// 	sms: {
									// 		marketing: {
									// 			consent: 'SUBSCRIBED',
									// 		},
									// 	},
									// },
								},
							},
						},
						channels: ['EMAIL', 'SMS'],
						// custom_source: 'Back in Stock',
					},
					relationships: {
						variant: {
							data: {
								type: 'catalog-variant',
								id: `$shopify:::$default:::${this.selectedVariantId}`,
							},
						},
					},
				},
			}

			const requestOptions = {
				method: 'POST',
				headers: {
					accept: 'application/vnd.api+json',
					'content-type': 'application/vnd.api+json',
					revision: new Date().toISOString().split('T')[0],
				},
				body: JSON.stringify(payload),
			}

			fetch(
				`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.publicKlavyioId}`,
				requestOptions
			)
				.then((res) => {
					// Success
					if (response.status == 202 && this.successTextEl) {
						this.successTextEl.classList.remove('l-hidden')

						l_debounce(() => {
							this.successTextEl.classList.add('l-hidden')
						}, 3000)()
					}

					// Handle error
					if (!response.status != 202 && this.errorTextEl) {
						this.errorTextEl.classList.remove('l-hidden')

						l_debounce(() => {
							this.errorTextEl.classList.add('l-hidden')
						}, 3000)()
					}
				})
				.catch((error) => {
					console.log(error)
				})
		}
	}

	customElements.define('l-product-back-in-stock-form', LTAProductBackInStockForm)
}

if (!customElements.get('l-product-price')) {
	class LTAProductPrice extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Elements
			this.globalNormalPriceEls = document.querySelectorAll(`[data-product-normal-price="${this.dataset.sectionId}"]`)
			this.priceEl = this.querySelector('[data-price]')
			this.normalPriceEl = this.querySelector('[data-normal-price]')
			this.saleTagEl = this.querySelector('[data-sale-tag]')

			// Data
			this.saleTagContent = this.dataset.saleTag
			this.normalPrice = parseInt(this.dataset.normalPrice)
			this.comparePrice = this.dataset.comparePrice ? parseInt(this.dataset.comparePrice) : 0
			this.addOnPrice = this.dataset.addOnPrice ? parseInt(this.dataset.addOnPrice) : 0

			this.updatePriceWithAddOns()

			// Events
			document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
			document.addEventListener('l:addon-products:changed', this.handleOnAddOnChange.bind(this))
		}

		handleOnVariantChange(event) {
			const { sectionId, html, variant } = event.detail

			const newPriceHTML = html.getElementById(`price-${sectionId}`)

			newPriceHTML.dataset.addOnPrice = this.addOnPrice
			this.outerHTML = newPriceHTML.outerHTML
		}

		handleOnAddOnChange(event) {
			this.addOnPrice = event.detail.totalPrice

			this.updatePriceWithAddOns()
		}

		updatePriceWithAddOns() {
			const newNormalPrice = this.normalPrice + this.addOnPrice
			const newNormalPriceFormatted = this.formatMoneyNoDecimals(newNormalPrice)
			const newComparePrice = this.comparePrice + this.addOnPrice

			this.priceEl.innerHTML = newNormalPriceFormatted
			if (this.comparePrice && this.normalPriceEl) {
				this.normalPriceEl.innerHTML = this.formatMoneyNoDecimals(newComparePrice)
			}
			if (this.saleTagEl) {
				const salePercentage = Math.round(((newComparePrice - newNormalPrice) / this.comparePrice) * 100)
				const salePrice = this.formatMoneyNoDecimals(newComparePrice - newNormalPrice)

				this.saleTagEl.innerHTML = this.saleTagContent
					.replace('[percentage]', salePercentage)
					.replace('[price]', salePrice)
			}

			this.globalNormalPriceEls.forEach((globalNormalPriceEl) => {
				globalNormalPriceEl.innerHTML = newNormalPriceFormatted
			})
		}
		formatMoneyNoDecimals(cents) {
    const format = window.theme?.money_format || '${{amount}}';
    const hasCents = cents % 100 !== 0;
    const value = hasCents
        ? (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        : Math.round(cents / 100).toLocaleString();
    return format.replace(/\{\{\s*amount\s*\}\}/, value);
}

	}

	customElements.define('l-product-price', LTAProductPrice)
}

/**********************************

* [PRODUCT BLOCK]: UPSELL PRODUCT

***********************************/

if (!customElements.get('l-upsell-products')) {
	customElements.define(
		'l-upsell-products',
		class LTAProductUpsells extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				if (this.dataset.url) this.loadRecommendations()
			}

			loadRecommendations() {
				const productListEl = this.querySelector('[data-products-list]')

				fetch(this.dataset.url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('.shopify-section')

						if (productListEl) {
							// Update atc buttons label
							recommendations.querySelectorAll('[data-atc]').forEach((atcBtn) => {
								atcBtn.innerHTML = this.dataset.atcBtnLabel
							})

							// Insert recommendations to product list
							productListEl.innerHTML = recommendations.innerHTML
							this.classList.remove('is-loading')
						}
					})
					.catch((e) => {
						this.remove()
					})
			}
		}
	)
}

if (!customElements.get('l-product-upsell-card')) {
	customElements.define(
		'l-product-upsell-card',
		class LTAProductUpsellCard extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.atcBtnEl = this.querySelector('[data-atc]')

				// Data
				this.selectedVariantID = this.dataset.defaultVariantId

				// Events
				this.atcBtnEl.addEventListener('click', this.onAddToCart.bind(this))
			}

			onAddToCart() {
				// Enable loading
				this.atcBtnEl.classList.add('is-loading')

				// Setup form data
				const config = L_ATC.handleConfigWithVariantId([{ id: this.selectedVariantID, quantity: 1 }])

				// Add selected variant to cart
				L_ATC.addToCart(config, 'l-product-upsell-card').finally(() => {
					this.atcBtnEl.classList.remove('is-loading')
				})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: SUBSCRIPTIONS

***********************************/

if (!customElements.get('l-subscriptions-selling-plans')) {
	customElements.define(
		'l-subscriptions-selling-plans',
		class LTASubscriptionSellingPlans extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.subscriptionOptionEls = this.querySelectorAll('[data-subscription-option]')

				// Data
				this.subscriptionData = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent)
				this.selectedVariantId = this.subscriptionData.id
				this.selectedSellingPlanId = this.querySelector('[name="selling_plan"]:checked')?.value

				// Setup functions
				this.optional__disableAppInputs()

				// Events
				this.addEventListener('change', this.handleOnChange.bind(this))
			}

			handleOnChange({ target }) {
				if (!target.hasAttribute('name') || !target.getAttribute('name') === 'selling_plan') return

				this.selectedSellingPlanId = target.value

				// TODO: Updates ???

				// Dispatch events
				this.dispatchSellingPlanIdChanged()
			}

			dispatchSellingPlanIdChanged() {
				this.dispatchEvent(
					new CustomEvent('l:sellingPlan:changed', {
						bubbles: true,
						detail: { sellingPlanId: this.selectedSellingPlanId },
					})
				)
			}

			optional__disableAppInputs() {
				document.addEventListener('sealsubs:subscription_widget_created', function (e) {
					const productEl = document.getElementById('product')
					const sealEls = productEl.querySelectorAll('.sealsubs-target-element')

					sealEls.forEach((sealEl) => {
						sealEl.classList.add('l-hidden')

						const sellingPlanInputs = sealEl.querySelectorAll('[name="selling_plan"]')
						sellingPlanInputs.forEach((input) => {
							input.setAttribute('disabled', 'disabled')
						})
					})
				})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: STICKY ATC

***********************************/
if (!customElements.get('l-product-sticky-atc')) {
	customElements.define(
		'l-product-sticky-atc',
		class LTAProductStickyATC extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.submitATCEls = this.querySelectorAll('[type="submit"]')
				this.productSectionEl = document.getElementById(`shopify-section-${this.dataset.sectionId}`)
				this.linkEls = this.querySelectorAll('[data-link]')

				// Setup functions
				this.handleOnScroll()
				this.onLinkFeatures()

				// Events
				this.submitATCEls.forEach((submitATCEl) => {
					submitATCEl.addEventListener('click', () => {
						submitATCEl.classList.add('is-loading')
					})
				})

				document.addEventListener('l:cart:updated', (event) => {
					this.submitATCEls.forEach((submitATCEl) => {
						submitATCEl.classList.remove('is-loading')
					})
				})
			}

			handleOnScroll() {
				const checkStickyATC = () => {
					const rect = this.productSectionEl.getBoundingClientRect()
					const scrollHeight = document.documentElement.scrollHeight
					const screenHeight = window.innerHeight
					const scrollPosition = window.scrollY || document.documentElement.scrollTop

					/*
					 * Condition:
					 * 1. Main atc button is of out screen
					 * 2. Sticky atc button is not at the bottom of the page
					 */
					const isVisible =
						rect.bottom <= window.innerHeight &&
						rect.top < -100 &&
						scrollPosition + screenHeight + this.offsetHeight < scrollHeight

					this.classList.toggle('is-sticky', isVisible)
				}

				// Run default check
				checkStickyATC()

				// Subscribe scroll event
				document.addEventListener(
					'scroll',
					l_debounce(() => {
						checkStickyATC()
					}, 50)
				)
			}

			onLinkFeatures() {
				this.linkEls.forEach((linkEl) => {
					linkEl.addEventListener('click', (event) => {
						const href = linkEl.getAttribute('href')
						if (href && href.startsWith('#')) {
							event.preventDefault()

							const targetId = href.substring(1)
							const targetElement = document.getElementById(targetId)

							if (targetElement) {
								targetElement.scrollIntoView({
									behavior: 'smooth',
									block: 'center',
								})
							}
						}
					})
				})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: PRODUCT ADD ONS

***********************************/
if (!customElements.get('l-product-addons')) {
	customElements.define(
		'l-product-addons',
		class LTAProductAddOns extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.variantEls = this.querySelectorAll('[data-variant]')

				// Events
				this.handleOnVariantCheck()
			}

			handleOnVariantCheck() {
				this.variantEls.forEach((variantEl) => {
					variantEl.addEventListener('click', (event) => {
						// Update selected variant
						const inputEl = variantEl.querySelector('input')
						const isChecked = variantEl.classList.toggle('is-checked')
						inputEl.toggleAttribute('disabled', !isChecked)

						// Calculate total price
						let totalPrice = 0
						this.variantEls.forEach((variantEl) => {
							const isChecked = variantEl.classList.contains('is-checked')
							const variantPrice = parseFloat(variantEl.dataset.variantPrice)

							totalPrice += isChecked ? variantPrice : 0
						})

						this.dispatchEvent(new CustomEvent('l:addon-products:changed', { bubbles: true, detail: { totalPrice } }))
					})
				})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: ___

***********************************/

if (!customElements.get('l-product-delivery-estimation')) {
	customElements.define(
		'l-product-delivery-estimation',
		class LTADeliveryEstimation extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.minDays = parseInt(this.dataset.minDays)
				this.maxDays = parseInt(this.dataset.maxDays)

				if (!this.minDays || !this.maxDays) return

				this.minDeliveryDateEl = this.querySelector('[data-min-delivery-text]')
				this.maxDeliveryDateEl = this.querySelector('[data-max-delivery-text]')

				const today = new Date()

				if (this.minDeliveryDateEl) {
					this.minDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.minDays)
				}

				if (this.maxDeliveryDateEl) {
					this.maxDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.maxDays)
				}
			}

			getNextWorkingDays(date, daysToAdd) {
				let nextDay = new Date(date)
				let addedDays = 0

				while (addedDays < daysToAdd) {
					nextDay.setDate(nextDay.getDate() + 1)

					if (nextDay.getDay() !== 0 && nextDay.getDay() !== 6) {
						addedDays++
					}
				}

				return this.formatDate(nextDay)
			}

			formatDate(date) {
				const day = date.getDate()
				const month = date.toLocaleString('en-GB', { month: 'short' })

				return `${day} ${month}`
			}
		}
	)
}

/************************************************************************************

* MODULE: PRODUCT MEDIA GALLERY

*************************************************************************************/
if (!customElements.get('l-product-media-gallery')) {
	customElements.define(
		'l-product-media-gallery',
		class LTAProductMediaGallery extends HTMLElement {
			constructor() {
				super()

				this.sectionId = this.dataset.sectionId
			}

			connectedCallback() {
				// Data
				this.initialIndex = parseInt(this.dataset.initialMediaIndex) || 0

				// Events
				document.addEventListener('l:vendor:loaded', () => {
					this.setupCarousel()
				})

				document.addEventListener('l:variant:changed', (event) => {
					this.onVariantChange(event.detail)
				})

				// Setup photo swipe
				this.setupPhotoSwipe()
			}

			setupCarousel() {
				// Setup carousel for thumbnail first
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					direction: 'vertical',
					mousewheel: true,
					initialSlide: this.initialIndex,
					navigation: {
						nextEl: this.querySelector('[data-thumbnails-next]'),
						prevEl: this.querySelector('[data-thumbnails-prev]'),
					},
				})

				// Setup carousel for main media
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 0,
					autoHeight: true,
					mousewheel: true,
					initialSlide: this.initialIndex,
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					// thumbs: {
					// 	swiper: this.thumbnailCarouselSwiper,
					// },
					pagination: {
						el: this.querySelector('[data-pagination]'),
						clickable: true,
						dynamicBullets: true,
						dynamicMainBullets: 5,
					},
				})

				// Setup controller
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper

				// Setup changed events
				this.handleSlideChangeEvent()
				this.updateThumbnailHeight()
				this.setupSyncThumbnails()
			}

			setupSyncThumbnails() {
				this.thumbnailSlides.forEach((thumbnailSlide, index0) => {
					thumbnailSlide.addEventListener('click', () => {
						this.mainCarouselSwiper?.slideTo?.(index0, 0, false)
					})
				})

				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeThumbnailSlideEl = this.thumbnailSlides[swiper.activeIndex]
					const activeIndex = activeThumbnailSlideEl.classList.contains('l-match-current-variant')
						? 0
						: swiper.activeIndex

					this.thumbnailCarousel?.slideTo?.(activeIndex, 0, false)
				})
			}

			setupPhotoSwipe(customData = []) {}

			handleSlideChangeEvent() {
				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = this.mediaSlides[swiper.activeIndex]

					l_pauseAllMedia()
					this.updateThumbnailHeight()
				})
			}

			updateThumbnailHeight() {
				this.style.setProperty('--l-media-vertical-thumbnail-height', `${this.mainCarousel.scrollHeight}px`)
			}

			onVariantChange(data) {
				this.selectedVariant = data.variant
				const matchMediaId = this.selectedVariant?.featured_media?.id

				const thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
				thumbnailSlides.forEach((thumbnailSlide) => {
					const mediaAlt = thumbnailSlide.dataset.mediaAlt

					thumbnailSlide.classList.toggle('l-match-current-variant', mediaAlt == this.selectedVariant.title)
				})

				// Slide to the media that matches the selected variant
				if (!matchMediaId) return

				this.mediaSlides.forEach((mediaSlide, index0) => {
					const mediaId = mediaSlide.dataset.mediaId

					if (mediaId == matchMediaId) {
						this.mainCarouselSwiper.slideTo(index0, 0, false)
					}
				})
			}
		}
	)
}

/************************************************************************************

* [PDP PAGE SECTIONS]:

*************************************************************************************/

/**********************************

* [SECTION]: RELATED PRODUCTS

***********************************/

if (!customElements.get('l-product-recommendations')) {
	customElements.define(
		'l-product-recommendations',
		class LTAProductRecommendations extends HTMLElement {
			observer = undefined

			constructor() {
				super()
			}

			connectedCallback() {
				this.initializeRecommendations()
			}

			initializeRecommendations() {
				this.observer?.unobserve(this)
				this.observer = new IntersectionObserver(
					(entries, observer) => {
						if (!entries[0].isIntersecting) return
						observer.unobserve(this)
						this.loadRecommendations()
					},
					{ rootMargin: '0px 0px 400px 0px' }
				)
				this.observer.observe(this)
			}

			loadRecommendations() {
				fetch(this.dataset.url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('l-product-recommendations')

						if (recommendations?.innerHTML.trim().length) {
							this.innerHTML = recommendations.innerHTML
						}
					})
					.catch((e) => {
						console.error(e)
					})
			}
		}
	)
}

